import type { JSONObject } from '@zoom/appssdk';

export const MULTIPLAYER_PROTOCOL_VERSION = 1;
export const MULTIPLAYER_PROMPT = 'Draw something that made you smile this week';
export const DRAWING_CHUNK_CHARACTERS = 500;
export const MAX_DRAWING_CHARACTERS = 200_000;
export const MAX_PLAYERS = 6;

export type MultiplayerPhase = 'lobby' | 'drawing' | 'sharing' | 'ended';
export type MultiplayerPlayerStatus = 'ready' | 'drawing' | 'submitted' | 'sharing' | 'shared';

export type MultiplayerPlayer = {
  id: string;
  name: string;
  status: MultiplayerPlayerStatus;
};

export type MultiplayerState = {
  gameId: string;
  hostId: string;
  revision: number;
  phase: MultiplayerPhase;
  prompt: string;
  players: MultiplayerPlayer[];
  sharingPlayerId: string | null;
};

type BaseMessage = {
  v: 1;
  f: string;
};

export type DiscoverMessage = BaseMessage & { t: 'discover'; n: string };
export type AnnounceMessage = BaseMessage & {
  t: 'announce';
  g: string;
  h: string;
  p: MultiplayerPhase;
};
export type JoinMessage = BaseMessage & { t: 'join'; g: string; n: string };
export type StateMessage = BaseMessage & {
  t: 'state';
  g: string;
  h: string;
  r: number;
  p: MultiplayerPhase;
  q: string;
  ps: Array<{ i: string; n: string; s: MultiplayerPlayerStatus }>;
  x: string;
};
export type DrawingChunkMessage = BaseMessage & {
  t: 'chunk';
  g: string;
  /** Owner of the artwork; differs from `f` when the host rebroadcasts it. */
  o: string;
  id: string;
  i: number;
  c: number;
  d: string;
};
export type ReactionMessage = BaseMessage & {
  t: 'reaction';
  g: string;
  a: string;
  x: string;
};
export type StateRequestMessage = BaseMessage & { t: 'request'; g: string };

export type MultiplayerMessage =
  | DiscoverMessage
  | AnnounceMessage
  | JoinMessage
  | StateMessage
  | DrawingChunkMessage
  | ReactionMessage
  | StateRequestMessage;

export function randomMultiplayerId(prefix: string): string {
  const bytes = crypto.getRandomValues(new Uint8Array(9));
  const value = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  return `${prefix}-${value}`;
}

export function normalizeDisplayName(name?: string): string {
  const cleaned = (name ?? '').trim().replace(/\s+/g, ' ') || 'Zoom participant';
  const encoder = new TextEncoder();
  let result = '';

  // State snapshots share Zoom's 1 KB limit. Bound by UTF-8 bytes rather than JS
  // characters so emoji-heavy names cannot overflow an otherwise valid roster.
  for (const character of Array.from(cleaned)) {
    const next = result + character;
    if (Array.from(next).length > 20 || encoder.encode(next).byteLength > 24) break;
    result = next;
  }

  return result || 'Participant';
}

export function stateToMessage(state: MultiplayerState, senderId: string): StateMessage {
  return {
    v: MULTIPLAYER_PROTOCOL_VERSION,
    t: 'state',
    f: senderId,
    g: state.gameId,
    h: state.hostId,
    r: state.revision,
    p: state.phase,
    q: state.prompt,
    ps: state.players.slice(0, MAX_PLAYERS).map((player) => ({
      i: player.id,
      n: normalizeDisplayName(player.name),
      s: player.status
    })),
    x: state.sharingPlayerId ?? ''
  };
}

export function messageToState(message: StateMessage): MultiplayerState {
  return {
    gameId: message.g,
    hostId: message.h,
    revision: message.r,
    phase: message.p,
    prompt: message.q,
    players: message.ps.map((player) => ({
      id: player.i,
      name: normalizeDisplayName(player.n),
      status: player.s
    })),
    sharingPlayerId: message.x || null
  };
}

export function drawingChunkMessages(
  gameId: string,
  senderId: string,
  ownerId: string,
  drawing: string
): DrawingChunkMessage[] {
  if (drawing.length > MAX_DRAWING_CHARACTERS) {
    throw new Error('Drawing is too large for Zoom messaging');
  }

  const transferId = randomMultiplayerId('drawing');
  const chunks = drawing.match(new RegExp(`.{1,${DRAWING_CHUNK_CHARACTERS}}`, 'gs')) ?? [''];

  return chunks.map((data, index) => ({
    v: MULTIPLAYER_PROTOCOL_VERSION,
    t: 'chunk',
    f: senderId,
    g: gameId,
    o: ownerId,
    id: transferId,
    i: index,
    c: chunks.length,
    d: data
  }));
}

export function isMultiplayerMessage(payload: unknown): payload is MultiplayerMessage {
  if (!payload || typeof payload !== 'object') return false;
  const candidate = payload as Partial<MultiplayerMessage>;
  return (
    candidate.v === MULTIPLAYER_PROTOCOL_VERSION &&
    typeof candidate.t === 'string' &&
    typeof candidate.f === 'string'
  );
}

export function asZoomPayload(message: MultiplayerMessage): JSONObject {
  return message as unknown as JSONObject;
}

export class DrawingAssembler {
  private transfers = new Map<
    string,
    {
      senderId: string;
      ownerId: string;
      count: number;
      chunks: Map<number, string>;
      createdAt: number;
    }
  >();

  add(message: DrawingChunkMessage): { ownerId: string; drawing: string } | null {
    if (
      message.c < 1 ||
      message.c > 500 ||
      message.i < 0 ||
      message.i >= message.c ||
      message.d.length > DRAWING_CHUNK_CHARACTERS
    ) {
      return null;
    }

    const key = `${message.g}:${message.f}:${message.id}`;
    const existing = this.transfers.get(key) ?? {
      senderId: message.f,
      ownerId: message.o,
      count: message.c,
      chunks: new Map<number, string>(),
      createdAt: Date.now()
    };

    if (existing.count !== message.c || existing.ownerId !== message.o) return null;
    existing.chunks.set(message.i, message.d);
    this.transfers.set(key, existing);
    this.prune();

    if (existing.chunks.size !== existing.count) return null;

    const drawing = Array.from(
      { length: existing.count },
      (_, index) => existing.chunks.get(index) ?? ''
    ).join('');
    this.transfers.delete(key);

    // Validate before the drawing reaches JSON.parse in PlayerSpotlight.
    try {
      const parsed = JSON.parse(drawing);
      if (!Array.isArray(parsed)) return null;
    } catch {
      return null;
    }

    return { ownerId: existing.ownerId, drawing };
  }

  private prune() {
    const cutoff = Date.now() - 30_000;
    for (const [key, transfer] of this.transfers) {
      if (transfer.createdAt < cutoff) this.transfers.delete(key);
    }
  }
}
